import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout/DashboardLayout";
import { LayoutDashboard, Menu as MenuIcon, ShoppingBag, CreditCard, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { ImageUploader } from "@/components/ImageUploader";

const items = (t) => [
  { to: "/restaurant", icon: LayoutDashboard, label: t("dashboard"), end: true, tid: "rest-nav-dashboard" },
  { to: "/restaurant/menu", icon: MenuIcon, label: t("menu"), tid: "rest-nav-menu" },
  { to: "/restaurant/orders", icon: ShoppingBag, label: t("orders"), tid: "rest-nav-orders" },
  { to: "/restaurant/subscription", icon: CreditCard, label: t("subscription"), tid: "rest-nav-subscription" },
];

export default function RestaurantMenu() {
  const { t } = useLang();
  const [restaurant, setRestaurant] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", category: "", price: "", image_url: "" });

  const load = async () => {
    const r = await api.get("/my/restaurant");
    if (r.data) {
      const full = await api.get(`/restaurants/${r.data.id}`);
      setRestaurant(full.data);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post(`/restaurants/${restaurant.id}/menu`, { ...form, price: Number(form.price) });
    setForm({ name: "", description: "", category: "", price: "", image_url: "" });
    setShowForm(false);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/restaurants/${restaurant.id}/menu/${id}`);
    load();
  };

  if (!restaurant) return <DashboardLayout items={items(t)} title={t("menu")}><div>Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout items={items(t)} title={t("menu")}>
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="font-bold text-lg">{restaurant.menu?.length || 0} items</div>
          <div className="text-sm text-[var(--so-text-soft)]">{restaurant.name}</div>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="so-btn-primary" data-testid="add-menu-toggle">
          <Plus className="w-4 h-4" /> {t("addMenuItem")}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="so-card p-5 mb-5 grid sm:grid-cols-2 gap-3" data-testid="menu-form">
          <input className="so-input" placeholder={t("name")} required value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="menu-name" />
          <input className="so-input" placeholder={t("category")} required value={form.category} onChange={e => setForm({...form, category: e.target.value})} data-testid="menu-category" />
          <input className="so-input sm:col-span-2" placeholder={t("description")} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="menu-description" />
          <input className="so-input" type="number" step="0.01" placeholder={t("price")} required value={form.price} onChange={e => setForm({...form, price: e.target.value})} data-testid="menu-price" />
          <div className="sm:col-span-2">
            <ImageUploader
              value={form.image_url}
              onChange={(url) => setForm({...form, image_url: url})}
              label="Menu photo"
              testId="menu-image-uploader"
              variant="square"
              lang={t("lang") === "ar" ? "ar" : "en"}
              maxWidth="240px"
            />
          </div>
          <button type="submit" className="so-btn-primary sm:col-span-2 justify-center" data-testid="menu-submit">Save item</button>
        </form>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {restaurant.menu?.map((m) => (
          <div key={m.id} className="so-card p-3 flex gap-3" data-testid={`menu-row-${m.id}`}>
            <img src={m.image_url} alt={m.name} className="w-16 h-16 rounded-lg object-cover" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{m.name}</div>
              <div className="text-xs text-[var(--so-text-soft)]">{m.category}</div>
              <div className="text-sm font-bold text-[var(--so-orange)] mt-1">${m.price}</div>
            </div>
            <button onClick={() => remove(m.id)} className="text-red-500 hover:text-red-700" data-testid={`del-menu-${m.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
